/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React, { useState, useMemo } from "react";
import { Grid, Stack, Typography } from "@mui/material";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikProps } from "formik";
import { FormProps } from "interfaces";
import {
  AccountingInterface,
  AccountingMovementInterface,
  AccountingStatusEnum,
} from "@malmur-technologies/medicalsystem-shared";
import { useTranslation } from "react-i18next";
import FormField from "components/generals/FormInput";
import Button from "components/generals/Button";
import FormActionButtons from "./custom/FormActionButtons";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { FormActionType } from "types";
import AccountingMovementsTotals from "components/views/AccountingMovementsTotals";

/**
 * Form component for managing accounting records.
 * Displays accounting information and associated movements with totals summary.
 * Supports view-only mode for reviewing accounting details.
 * @param {FormProps<AccountingInterface>} props - Component props including model, action, and onCloseAction
 * @returns {React.ReactElement} The accounting form component
 */
const AccountingsForm: React.FC<FormProps<AccountingInterface>> = ({
  model,
  action,
  onCloseAction,
}) => {
  const { t } = useTranslation();
  const [isLoading] = useState<boolean>(false);

  const movements: AccountingMovementInterface[] =
    model && (model as any).movements ? (model as any).movements : [];

  const initialValues = useMemo(
    (): AccountingInterface => ({
      id: model?.id || "",
      startingDate: model?.startingDate || new Date(),
      closingDate: model?.closingDate || new Date(),
      patient: model?.patient || { id: "" },
      status: model?.status || AccountingStatusEnum.OPEN,
    }),
    [model]
  );

  const onSubmit = (values: AccountingInterface) => {
    onCloseAction();
  };

  return (
    <FormikForm initialValues={initialValues} onSubmit={onSubmit} validationSchema={undefined}>
      {(formikProps: FormikProps<AccountingInterface>) => (
        <Form id="accountingsForm" autoComplete="off">
          <Stack spacing={2} mb={3}>
            <Typography variant="h4">
              {action === FormActionType.NEW
                ? t("accountingsForm.newAccount")
                : t("accountingsForm.editAccount")}
            </Typography>
            <AccountingMovementsTotals movements={movements} />
          </Stack>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormField name="status" label={t("accountingsForm.status")} type="text" fullWidth />
            </Grid>
          </Grid>

          <FormActionButtons showSubmitButton={false} closeAction={onCloseAction} />
          <Stack direction="row" spacing={2} mt={2}>
            <PermissionWrapper requiredPermissions={[]}>
              <Button type="submit" variant="contained" color="info" disabled={isLoading}>
                {action === FormActionType.NEW
                  ? t("stepperFormActionButtons.submit")
                  : t("stepperFormActionButtons.update")}
              </Button>
            </PermissionWrapper>
            <Button variant="outlined" onClick={onCloseAction}>
              {t("common.cancel")}
            </Button>
          </Stack>
        </Form>
      )}
    </FormikForm>
  );
};

export default AccountingsForm;
